/* eslint @typescript-eslint/no-explicit-any: 0 */

import { Configure, connectStateResults } from 'react-instantsearch-dom';

import SearchInteractionEvent from '~/events/SearchInteractionEvent';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';

import MenuLink from '../MenuLink';
import IfHits from './IfHits';
import { Index } from './InstantSearch';
import SearchPanelHits from './SearchPanelHits';
import SearchResult from './SearchResult';

type SearchOption = {
  clickAnalytics: boolean;
  filters: string;
  hitsPerPage: number;
};

type RedirectLinkOption = {
  pathname: string;
  search: string;
};

type SearchItemType = {
  clothing: SearchOption;
  footwear: SearchOption;
  news: SearchOption;
};

type RedirectLinksItemType = {
  clothing: RedirectLinkOption;
  footwear: RedirectLinkOption;
  news: RedirectLinkOption;
};

type SearchTypes = {
  redirectLinks?: RedirectLinksItemType;
  searchOptions?: SearchItemType;
};

type SearchPanelResultsTypesProps = {
  close: any;
  displayState: string;
  isLargeBreakpoint: boolean;
  isMediumBreakpoint: boolean;
  query: any;
  redirectLinks: RedirectLinksItemType;
  searchCondition: (searchResults: any) => boolean;
  searchOptions: SearchItemType;
};

type SearchPanelSelectionBoxProps = {
  close: any;
  displayState: string;
  indexId: string;
  isLargeBreakpoint: boolean;
  isMediumBreakpoint: boolean;
  query: any;
  searchCondition: (searchResults: any) => boolean;
  searchOptions: SearchItemType;
  title: string;
  to: { pathname: string };
  type: string;
};

export function searchTypes({ redirectLinks, searchOptions }: SearchTypes) {
  return [
    {
      indexId: 'footwear',
      label: 'Footwear',
      searchOptions: searchOptions?.footwear,
      title: 'Footwear',
      to: redirectLinks?.footwear,
      type: 'footwear'
    },
    {
      indexId: 'news',
      label: 'News',
      searchOptions: searchOptions?.news,
      title: 'News',
      to: redirectLinks?.news,
      type: 'news'
    },
    {
      indexId: 'clothing',
      label: 'Clothing',
      searchOptions: searchOptions?.clothing,
      title: 'Clothing',
      to: redirectLinks?.clothing,
      type: 'clothing'
    }
  ];
}

const ViewMoreButton = connectStateResults(
  ({
    searchResults,
    to,
    onClick,
    query
  }: {
    onClick: () => void;
    query?: string;
    searchResults: any;
    to: { pathname: string };
  }) => {
    const { isBot } = useRootData();
    const user = useUser();

    const viewMoreUrl = new URL(`${window.location.origin}${to.pathname}`);
    if (query) {
      viewMoreUrl.searchParams.append('q', query);
    }

    function handleViewMoreClick() {
      if (query) {
        new SearchInteractionEvent({
          destinationPath: viewMoreUrl.href,
          gesture: 'click',
          guestId: user.guestId,
          isBot,
          placement: 'view_more',
          searchTerm: query
        }).send();
      }

      onClick();
    }

    return searchResults && searchResults.nbHits ? (
      <div className="last:mt-6">
        <MenuLink
          link={viewMoreUrl.href}
          onClick={handleViewMoreClick}
          title="View More"
        />
      </div>
    ) : null;
  }
);

function NoResultsForType({
  isMediumBreakpoint,
  title
}: {
  isMediumBreakpoint: boolean;
  title: string;
}) {
  return (
    <>
      {isMediumBreakpoint && (
        <span className="mb-4 flex items-baseline gap-[6px] font-heading text-[16px] font-medium leading-5">
          {title}
        </span>
      )}
      <p className="text-label mt-4 font-medium lg:mt-0">No {title} results</p>
    </>
  );
}

export function SearchPanelSelectionBox({
  displayState,
  isMediumBreakpoint,
  close,
  query,
  to,
  title,
  searchOptions,
  type,
  indexId,
  searchCondition
}: SearchPanelSelectionBoxProps) {
  return (
    <Index indexId={indexId}>
      <Configure {...searchOptions} />
      <div
        className={`${displayState === type ? 'block' : 'hidden'} md:block ${
          type === 'clothing' ? '' : 'md:pr-[10px]'
        }`}
        id={type}
      >
        <IfHits condition={searchCondition}>
          <ul>
            <SearchPanelHits
              hitComponent={SearchResult}
              hitProps={{
                onClick: close,
                query: query,
                showNbResults: !isMediumBreakpoint,
                showTitle: isMediumBreakpoint,
                title,
                type: 'searchOverlay'
              }}
              notFoundComponent={() => (
                <NoResultsForType
                  isMediumBreakpoint={isMediumBreakpoint}
                  title={title}
                />
              )}
            />
            <ViewMoreButton to={to} onClick={close} query={query} />
          </ul>
        </IfHits>
      </div>
    </Index>
  );
}

export default function SearchPanelResultsTypes({
  redirectLinks,
  searchOptions,
  isLargeBreakpoint,
  isMediumBreakpoint,
  displayState,
  close,
  query,
  searchCondition
}: SearchPanelResultsTypesProps) {
  const items = searchTypes({ redirectLinks, searchOptions });

  return (
    <>
      {items.map((item: any = {}) => {
        const { to, title, searchOptions, type, indexId } = item;

        return (
          <SearchPanelSelectionBox
            key={type}
            isLargeBreakpoint={isLargeBreakpoint}
            displayState={displayState}
            isMediumBreakpoint={isMediumBreakpoint}
            close={close}
            query={query}
            to={to}
            title={title}
            searchOptions={searchOptions}
            type={type}
            indexId={indexId}
            searchCondition={searchCondition}
          />
        );
      })}
    </>
  );
}
